import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Award, BookOpen, Heart, Users, Target, Lightbulb } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15 } })
};

const milestones = [
  { icon: Users, label: '10,000+', desc: 'Lives Transformed' },
  { icon: BookOpen, label: '15+', desc: 'Programs Created' },
  { icon: Award, label: '10+', desc: 'Years Experience' },
  { icon: Heart, label: '50+', desc: 'Countries Reached' },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative py-24 sm:py-32 bg-gradient-to-br from-indigo-50 via-white to-amber-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
              <span className="text-xs sm:text-sm tracking-[0.2em] uppercase text-indigo-600 font-semibold">About Dr. Dinesh</span>
              <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.1] text-slate-900 mt-4 mb-6">
                A Journey of{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-amber-600">Transformation</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-4">
                Dr. Dinesh Mungath is a certified mindset transformation coach, subconscious reprogramming expert, and manifestation practitioner who has dedicated his life to helping people break free from limitations and step into their full potential.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                With a unique blend of psychology, neuroscience, and energy awareness principles, Dr. Dinesh has developed a practical, science-backed approach to manifestation that has transformed over 10,000 lives across 50+ countries.
              </p>
              <Link to="/courses">
                <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800 gap-2" data-testid="about-explore-btn">
                  Explore Programs <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1} className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_manifest-dinesh/artifacts/bca2afwc_12.png"
                alt="Dr. Dinesh Mungath"
                className="rounded-3xl w-full object-cover object-top aspect-[3/4] shadow-2xl bg-slate-100"
                data-testid="about-hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {milestones.map((m, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                  <m.icon className="w-6 h-6 text-indigo-600" />
                </div>
                <p className="font-serif text-3xl font-bold text-slate-900">{m.label}</p>
                <p className="text-sm text-slate-500 mt-1">{m.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 sm:py-32 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-slate-900 mb-8 text-center">The Story Behind the Mission</h2>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} className="space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed">
            <p>
              Like many of us, Dr. Dinesh's journey began from a place of struggle. Despite academic success and a promising career, he found himself trapped in cycles of stress, financial anxiety, and a deep sense of unfulfillment. Traditional approaches offered temporary relief but never addressed the root cause.
            </p>
            <p>
              Everything changed when he discovered the power of the subconscious mind. Through years of research, personal experimentation, and training under world-renowned experts in psychology, NLP, and energy healing, he developed a comprehensive framework that combines the best of modern neuroscience with timeless wisdom traditions.
            </p>
            <p>
              Today, Dr. Dinesh is on a mission to make these transformative tools accessible to everyone. His programs are designed to be practical, grounded, and results-oriented - stripping away the mysticism while preserving the profound effectiveness of these techniques.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: 'Mission', text: 'To empower 1 million individuals worldwide to break free from limiting beliefs, unlock their subconscious potential, and create lives of abundance, purpose, and joy.' },
              { icon: Lightbulb, title: 'Vision', text: 'A world where every person understands and harnesses the power of their mind to consciously create their reality, leading to collective transformation and elevated consciousness.' },
              { icon: Heart, title: 'Values', text: 'Authenticity, compassion, and evidence-based practice. Every technique shared is personally tested, scientifically grounded, and delivered with genuine care for your transformation.' },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} className="bg-slate-50 rounded-2xl p-8 sm:p-10 border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mb-6">
                  <item.icon className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-4">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-slate-900 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mb-6">Ready to Begin?</h2>
            <p className="text-lg text-slate-300 mb-10">Take the first step toward the life you were meant to live.</p>
            <Link to="/courses">
              <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-base gap-2" data-testid="about-cta-btn">
                Explore Programs <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
